import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { LearnerExperienceLayoutComponent } from './layout/learner-experience-layout.component';
import { AuthGuardService } from '@app/routing/services/auth-guard.service';
import { RouterModule } from '@angular/router';
import { OnboardingGuardService } from '@app/routing/services/onboarding-guard.service';
import { OnboardingRequiredGuard } from '@app/learner-home/guards/onboarding-required.guard';
import { OpportunitiesViewGuardService } from '@app/routing/services/opportunities-view-guard.service';
import { TeamViewGuardService } from '@app/routing/services/team-view.guard.service';
import { OrgFaqViewComponent } from '@app/orgs/components/org-faq-view/org-faq-view.component';
import { CareerMobilityAccessGuardService } from '@app/routing/services/career-mobility-access-guard.service';
import { FeedbackModule } from '@app/feedback/feedback.module';
import { SessionModule } from '@app/session/session.module';
import { SharedModule } from '@app/shared/shared.module';
import { LayoutModule } from '@app/layout/layout.module';
import { ChannelHeaderModule } from '@app/channel/channel-header.module';
import { SkillScaleLevelChangeModule } from '@app/profile/components/skill-scale-level-change-modal/skill-scale-level-change.module';
import { InputPageWrapper } from '@app/inputs/components/input-page-wrapper/input-page-wrapper.component';
import { PathwayAddEditFormModule } from '@app/pathways/components/settings/pathway-add-edit-form-modal/pathway-add-edit-form.module';

import { LxpNavComponent } from './layout/components/lxp-nav/lxp-nav.component';
import { NotificationSharedModule } from '@app/notifications/notification-shared.module';
import { GlobalAddModule } from '@app/global-add/global-add.module';
import { TopNavigationMenuComponent } from './layout/components/top-navigation-menu/top-navigation-menu.component';
import { PathwayPageGuard } from '@app/pathways/routes/guards/pathway-page.guard';
import { DegreedAssistantComponent } from './pages/degreed-assistant/degreed-assistant.component';

const LXP_ROUTES = [
  {
    path: '',
    component: LearnerExperienceLayoutComponent,
    canActivate: [OnboardingRequiredGuard],
    children: [
      {
        path: ':id/dashboard',
        loadChildren: () =>
          import('../learner-home/learner-home.module').then(
            (m) => m.LearnerHomeModule
          ),
        canActivate: [AuthGuardService],
        canLoad: [AuthGuardService],
      },
      {
        path: 'profile',
        loadChildren: () =>
          import('../profile/profile.module').then((m) => {
            return m.ProfileModule;
          }),
      },
      {
        path: 'career',
        loadChildren: () =>
          import('../career/career.module').then((m) => {
            return m.CareerModule;
          }),
        canActivate: [AuthGuardService, CareerMobilityAccessGuardService],
      },
      {
        path: 'getstarted',
        loadChildren: () =>
          import('../onboarding/onboarding.module').then((m) => {
            return m.OnboardingModule;
          }),
        canActivate: [OnboardingGuardService],
      },
      // Todo: groupModule is being imported globally in app.module.
      // Clean up PD-71947 should be done to remove groupModule and use it here instead.
      {
        path: 'groups',
        loadChildren: () =>
          import('../groups/groups.module').then((m) => {
            return m.GroupsModule;
          }),
      },
      {
        path: 'learning',
        loadChildren: () =>
          import('../search/search.module').then((m) => {
            return m.SearchModule;
          }),
      },
      {
        path: 'notifications',
        loadChildren: () =>
          import('../notifications/notification.module').then((m) => {
            return m.NotificationModule;
          }),
        canActivate: [AuthGuardService],
      },
      {
        path: 'opportunities',

        loadChildren: () =>
          import('../opportunities/opportunities.module').then((m) => {
            return m.OpportunitiesModule;
          }),
        canActivate: [AuthGuardService, OpportunitiesViewGuardService],
        canLoad: [AuthGuardService, OpportunitiesViewGuardService],
      },
      {
        path: 'pathway',
        loadChildren: () =>
          import('../pathways/components/page/pathway-page.module').then(
            (m) => {
              return m.PathwayPageModule;
            }
          ),
      },

      // TODO NGX 14 UPGRADE: (CCO)
      // -- Error: NG04014: Invalid configuration of route 'paths/**'. One of the following must
      // -- be provided: component, loadComponent, redirectTo, children or loadChildren.
      // -- Currently updated to redirect to home

      // Catch for all old pathway routes, page guard handles redirects
      {
        path: 'paths',
        children: [
          {
            path: '**',
            redirectTo: '/',
          },
        ],
        canActivate: [PathwayPageGuard],
      },
      {
        path: 'skillreview',
        loadChildren: () =>
          import('../evaluation/evaluation.module').then((m) => {
            return m.EvaluationModule;
          }),
        canActivate: [AuthGuardService],
      },
      {
        path: 'credential',
        loadChildren: () =>
          import('../credential/credential.module').then(
            (m) => m.CredentialModule
          ),
      },

      {
        path: 'plan',
        loadChildren: () =>
          import('../flex-framework/flex-framework.module').then((m) => {
            return m.FlexFrameworkModule;
          }),
      },
      {
        path: 'explore',
        loadChildren: () =>
          import('../flex-framework/flex-framework.module').then((m) => {
            return m.FlexFrameworkModule;
          }),
      },
      {
        path: 'reporting',
        loadChildren: () =>
          import(
            '../reporting/downloadable/reporting-downloadable.module'
          ).then((m) => m.ReportingDownloadableModule),
      },
      {
        path: 'team',
        loadChildren: () =>
          import('../team/team.module').then((m) => {
            return m.TeamModule;
          }),
        canActivate: [AuthGuardService, TeamViewGuardService],
        canLoad: [AuthGuardService, TeamViewGuardService],
      },
      {
        path: 'author',
        loadChildren: () =>
          import('../author/author.module').then((m) => m.AuthorModule),
      },
      {
        path: 'license',
        loadChildren: () =>
          import('../license/license.module').then((m) => {
            return m.LicenseModule;
          }),
        canActivate: [AuthGuardService],
        canLoad: [AuthGuardService],
      },
      {
        path: 'faq/:orgId',
        component: OrgFaqViewComponent,
      },
      {
        path: 'resource',
        loadChildren: () =>
          import('../tags/tags.module').then((m) => {
            return m.TagsModule;
          }),
      },
      {
        path: 'invite',
        loadChildren: () =>
          import('../invite/invite.module').then((m) => {
            return m.InviteModule;
          }),
      },
      {
        path: 'skillstandards',
        loadChildren: () =>
          import('../skill-standards/skill-standards.module').then((m) => {
            return m.SkillStandardsModule;
          }),
      },
      {
        path: 'academies',
        loadChildren: () =>
          import('../academies/academies.module').then(
            (m) => m.AcademiesModule
          ),
      },
      {
        path: 'degreed-assistant',
        loadChildren: () =>
          import('./pages/degreed-assistant/degreed-assistant.module').then(
            (m) => m.DegreedAssistantModule
          ),
      },

      /**
       * Input routes added here to prevent content urls from being removed from browser because there was no matching ngx routes here.
       * Once ngx migration are completed for these routes the AjsPlaceholders will be updated to the new ngx input components.
       * Bug reference: https://degreedjira.atlassian.net/browse/PD-75345
       */
      {
        path: 'articles',
        data: { type: 'article' },
        component: InputPageWrapper,
      },
      {
        path: 'articles/:title',
        data: { type: 'article' },
        component: InputPageWrapper,
      },
      {
        path: 'videos',
        data: { type: 'video' },
        component: InputPageWrapper,
      },
      {
        path: 'videos/:title',
        data: { type: 'video' },
        component: InputPageWrapper,
      },
      {
        path: 'books',
        data: { type: 'book' },
        component: InputPageWrapper,
      },
      {
        path: 'books/:title',
        data: { type: 'book' },
        component: InputPageWrapper,
      },
      {
        path: 'events',
        data: { type: 'event' },
        component: InputPageWrapper,
      },
      {
        path: 'events/:title',
        data: { type: 'event' },
        component: InputPageWrapper,
      },
      {
        path: 'episodes',
        data: { type: 'episode' },
        component: InputPageWrapper,
      },
      {
        path: 'episodes/:title',
        data: { type: 'episode' },
        component: InputPageWrapper,
      },
      {
        path: 'courses',
        data: { type: 'course' },
        component: InputPageWrapper,
      },
      {
        path: 'courses/:title',
        data: { type: 'course' },
        component: InputPageWrapper,
      },
      {
        path: 'assessments',
        data: { type: 'assessment' },
        component: InputPageWrapper,
      },
      {
        path: 'assessments/:title',
        data: { type: 'assessment' },
        component: InputPageWrapper,
      },
      {
        path: 'input/post',
        data: { type: 'post' },
        component: InputPageWrapper,
      },
      {
        path: 'input/post/:title',
        data: { type: 'post' },
        component: InputPageWrapper,
      },
      {
        path: 'tasks/:title',
        data: { type: 'task' },
        component: InputPageWrapper,
      },
    ],
  },
];

@NgModule({
  declarations: [
    LearnerExperienceLayoutComponent,
    LxpNavComponent,
    TopNavigationMenuComponent,
  ],
  imports: [
    RouterModule.forChild(LXP_ROUTES),
    LayoutModule,
    SharedModule,
    CommonModule,
    FeedbackModule,
    SessionModule,
    ChannelHeaderModule,
    NotificationSharedModule,
    PathwayAddEditFormModule, // TODO: refactor away this module: It's currently in use by global search and both content cards and learning resource cards.
    GlobalAddModule,
    SkillScaleLevelChangeModule,
  ],
  providers: [
    AuthGuardService,
    OnboardingGuardService,
    OpportunitiesViewGuardService,
    CareerMobilityAccessGuardService,
  ],
})
export class LearnerExperienceModule {}
